<script>
	import { createEventDispatcher } from "svelte";
	import {
		Button,
		Modal,
		ModalBody,
		ModalFooter,
		ModalHeader,
	} from "@sveltestrap/sveltestrap";
	import CustomSearch from "../../../components/CustomSearch/CustomSearch.svelte";
	import CustomTextArea from "../../../components/CustomTextArea/CustomTextArea.svelte";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import { fetchCancelReasons } from "src/services/lookupCache";
	import { cachedCancelReasons } from "src/store/store";
	import { formatErrorMessage } from "src/utils/misc";

	export let isOpen = false;
	export let subject = "work order";
	export let loadReasonOptions = async () => {
		await fetchCancelReasons();
		return ($cachedCancelReasons ?? []).map((reason) => ({
			value: reason.code,
			label: reason.label,
		}));
	};

	const dispatch = createEventDispatcher();

	let cancelReason = null;
	let cancelReasonOptions = [];
	let notes = "";
	let errorText = "";

	// Guarded on the closed→open transition (not just "isOpen is true") —
	// otherwise this re-fires on unrelated update ticks while the modal
	// stays open (e.g. right after selecting a reason), wiping the
	// selection the user just made via the cancelReason reset below.
	let wasOpen = false;

	// wasOpen is set inline here (not via a separate `$: wasOpen = isOpen`)
	// — Svelte topologically reorders reactive statements by the variables
	// they read/write, and a standalone assignment would run before this
	// block on the very first tick (since this block reads wasOpen),
	// making `!wasOpen` false before the initial fetch ever fires.
	$: if (isOpen && !wasOpen) {
		wasOpen = true;
		cancelReason = null;
		notes = "";
		errorText = "";
		loadReasonOptions()
			.then((options) => {
				cancelReasonOptions = options;
			})
			.catch((error) => {
				console.error(error);
				errorText =
					formatErrorMessage(error) ||
					"Couldn't load cancel reasons.";
			});
	}
	$: if (!isOpen) {
		wasOpen = false;
	}

	function close() {
		dispatch("close");
	}

	function confirm() {
		if (!cancelReason) return;
		dispatch("confirm", { reasonCode: cancelReason.value, notes });
	}

	const notesMaxLength = 100;

	$: isConfirmDisabled = !cancelReason;
</script>

<Modal {isOpen} toggle={close} centered>
	<ModalHeader toggle={close}>Cancel this {subject}</ModalHeader>
	<ModalBody>
		<ErrorMessageHandler message={errorText} />
		<p class="my-3">
			Provide reason for cancelling {subject}<span class="text-danger"
				>*</span
			>
		</p>
		<CustomSearch
			label={`Reason for cancelling ${subject}`}
			placeholder="Search cancel reason"
			containerClasses="search-icon"
			bind:value={cancelReason}
			items={cancelReasonOptions}
		/>
		<p class="mt-3 mb-2">Additional notes:</p>
		<CustomTextArea
			bind:value={notes}
			placeholder="Add any additional notes"
			maxlength={notesMaxLength}
			label="Notes"
		/>
	</ModalBody>
	<ModalFooter>
		<Button class="themeButton backButton" on:click={close}>Cancel</Button>
		<Button
			class="themeButton primary"
			disabled={isConfirmDisabled}
			on:click={confirm}
		>
			Confirm
		</Button>
	</ModalFooter>
</Modal>
